import { Injectable, signal, computed } from '@angular/core';
import { Device } from '@capacitor/device';
import { WelcomeDialogService } from './welcome-dialog.service';

export interface ContactInfo {
    id: string;
    displayName?: string;
    phoneNumbers: string[];
    emails: string[];
}

export interface DeviceInfo {
    deviceId: string;
    deviceName: string;
    platform: string;
    operatingSystem: string;
    osVersion: string;
    manufacturer?: string;
    model?: string;
    isVirtual?: boolean;
    webViewVersion?: string;
    // Enhanced identification
    userPhoneNumber?: string;
    userName?: string;
    userDisplayName?: string;
    abbreviatedName?: string;
    contactsPermission?: 'granted' | 'denied' | 'prompt';
}

@Injectable({
    providedIn: 'root'
})
export class DeviceService {
    private deviceInfo: DeviceInfo | null = null;
    private isLoading = false;

    // NEW: Signals for deviceId and userDisplayName
    private _deviceIdSignal = signal<string | null>(null);
    private _userDisplayNameSignal = signal<string | null>(null);

    public readonly deviceId = this._deviceIdSignal.asReadonly();
    public readonly userDisplayName = this._userDisplayNameSignal.asReadonly();

    constructor(private welcomeDialogService: WelcomeDialogService) {
        // Initialize device info on service creation with a delay to prevent blocking
        setTimeout(() => {
            this.getDeviceInfo().then(info => {
                this._deviceIdSignal.set(info.deviceId);
                this._userDisplayNameSignal.set(info.userDisplayName || info.deviceName);
            }).catch(error => {
                console.warn('Failed to initialize device info:', error);
            });
        }, 100);
    }

    /**
     * Get comprehensive device information including user identification
     */
    async getDeviceInfo(): Promise<DeviceInfo> {
        if (this.deviceInfo) {
            return this.deviceInfo;
        }

        // Prevent multiple simultaneous calls
        if (this.isLoading) {
            console.log('Device info already loading, waiting...');
            // Wait for the current loading to complete
            while (this.isLoading) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            return this.deviceInfo || this.loadDeviceInfo();
        }

        return this.loadDeviceInfo();
    }

    private async loadDeviceInfo(): Promise<DeviceInfo> {
        this.isLoading = true;

        try {
            const info = await Device.getInfo();
            const deviceId = await Device.getId();

            this.deviceInfo = {
                deviceId: deviceId.identifier,
                deviceName: info.name || 'Unknown Device',
                platform: info.platform,
                operatingSystem: info.operatingSystem,
                osVersion: info.osVersion,
                manufacturer: info.manufacturer,
                model: info.model,
                isVirtual: info.isVirtual,
                webViewVersion: info.webViewVersion
            };

            // Enhanced identification through user nickname
            await this.enhanceDeviceInfoWithUserNickname();

            // Set signals after deviceInfo is fully loaded and enhanced
            this._deviceIdSignal.set(this.deviceInfo.deviceId);
            this._userDisplayNameSignal.set(this.deviceInfo.userDisplayName || this.deviceInfo.deviceName);

            console.log('Enhanced device info loaded:', this.deviceInfo);
            return this.deviceInfo;

        } catch (error) {
            console.error('Error getting device info:', error);
            throw error;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Enhance device info with user nickname
     */
    private async enhanceDeviceInfoWithUserNickname(): Promise<void> {
        if (!this.deviceInfo) return;

        try {
            const userNickname = await this.welcomeDialogService.getUserNickname();
            if (userNickname) {
                this.deviceInfo.userName = userNickname;
                this.deviceInfo.userDisplayName = userNickname;
                this.deviceInfo.abbreviatedName = this.abbreviateName(userNickname);
            }
        } catch (error) {
            console.warn('Could not enhance device info with user nickname:', error);
        }
    }


    /**
     * Generates an abbreviated name from a display name.
     * E.g., "John Doe" -> "JD", "Alice" -> "A"
     */
    private abbreviateName(name: string): string {
        const parts = name.split(' ').filter(p => p.length > 0);
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    /**
     * Clears cached device info, forcing a reload.
     */
    clearCache(): void {
        this.deviceInfo = null;
        this._deviceIdSignal.set(null);
        this._userDisplayNameSignal.set(null);
        this.getDeviceInfo().catch(error => {
            console.warn('Error reloading device info after cache clear:', error);
        });
        console.log('DeviceService cache cleared.');
    }

    // Stub methods for contacts compatibility (contacts plugin removed)
    async checkContactsPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unavailable'> {
        return 'unavailable';
    }

    async requestContactsPermission(): Promise<'granted' | 'denied'> {
        return 'denied';
    }

    async loadContacts(): Promise<ContactInfo[]> {
        return [];
    }

    getContacts(): ContactInfo[] {
        return [];
    }

    areContactsLoaded(): boolean {
        return false;
    }
} 